import React, { useState, useEffect, useCallback } from "react";
import {
  getEmployees,
  getDruckerData,
  toggleDruckerExclude,
  addDruckerContact,
  deleteDruckerContact,
  syncDrucker,
  testDruckerConn,
  probeDrucker,
} from "../services/api";
import ProgressBar from "../components/ProgressBar";

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  th:  { padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#444", background: "#f8f9fa" },
  td:  { padding: "7px 12px", borderBottom: "1px solid #f0f0f0" },
  btn: (bg, color, border = "none") => ({
    padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6,
    background: bg, color, border, cursor: "pointer",
  }),
};

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("de-DE");
}

// ─── Hauptkomponente ─────────────────────────────────────────────────────────
export default function DruckerPage() {
  const [employees,  setEmployees]  = useState([]);
  const [excluded,   setExcluded]   = useState([]);
  const [manual,     setManual]     = useState([]);
  const [lastSync,   setLastSync]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  // Sync-Status
  const [syncing,    setSyncing]    = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError,  setSyncError]  = useState(null);
  const [syncProgress, setSyncProgress] = useState(null); // { batch, remaining, planTotal }

  // Test-Verbindung
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Probe (Pfad-Diagnose)
  const [probing,    setProbing]    = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  // Modal: Manuellen Eintrag hinzufügen
  const [showModal,  setShowModal]  = useState(false);
  const [newName,    setNewName]    = useState("");
  const [newEmail,   setNewEmail]   = useState("");
  const [saving,     setSaving]     = useState(false);

  // ── Daten laden ────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empRes, druckerRes] = await Promise.all([
        getEmployees({ status: "active" }),
        getDruckerData(),
      ]);
      setEmployees(empRes.data.employees || []);
      setExcluded(druckerRes.data.excluded || []);
      setManual(druckerRes.data.manual || []);
      setLastSync(druckerRes.data.lastSync);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Ausschluss togglen ─────────────────────────────────────────────────────
  const handleToggleExclude = async (email) => {
    try {
      const res = await toggleDruckerExclude(email);
      setExcluded((prev) =>
        res.data.excluded
          ? [...prev, email.toLowerCase()]
          : prev.filter((e) => e !== email.toLowerCase())
      );
    } catch (err) {
      alert("Fehler: " + (err.response?.data?.error || err.message));
    }
  };

  // ── Manuellen Kontakt hinzufügen ───────────────────────────────────────────
  const handleAddManual = async () => {
    if (!newName.trim() || !newEmail.trim()) return;
    setSaving(true);
    try {
      const res = await addDruckerContact(newName.trim(), newEmail.trim());
      setManual((prev) => [...prev, res.data.contact]);
      setNewName("");
      setNewEmail("");
      setShowModal(false);
    } catch (err) {
      alert("Fehler: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Manuellen Kontakt löschen ──────────────────────────────────────────────
  const handleDeleteManual = async (id) => {
    if (!window.confirm("Kontakt wirklich entfernen?")) return;
    try {
      await deleteDruckerContact(id);
      setManual((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      alert("Fehler: " + (err.response?.data?.error || err.message));
    }
  };

  // ── Verbindungstest ────────────────────────────────────────────────────────
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testDruckerConn();
      setTestResult({ ok: true, msg: `✅ Drucker erreichbar (HTTP ${res.data.status}) via ${res.data.protocol?.toUpperCase()}` });
    } catch (err) {
      setTestResult({ ok: false, msg: `❌ ${err.response?.data?.error || err.message}` });
    } finally {
      setTesting(false);
    }
  };

  // ── Pfad-Diagnose ──────────────────────────────────────────────────────────
  const handleProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    try {
      const res = await probeDrucker();
      setProbeResult(res.data.results);
    } catch (err) {
      setProbeResult([{ error: err.response?.data?.error || err.message }]);
    } finally {
      setProbing(false);
    }
  };

  // ── Sync ───────────────────────────────────────────────────────────────────
  // Ein großer Rückstand (z.B. einmaliger Aufräum-Sync) übersteigt sowohl die
  // Belastbarkeit der alten Druckerfirmware als auch das Timeout des
  // Reverse-Proxys, wenn man ihn in EINEM Request abarbeitet. Deshalb ruft das
  // Frontend /drucker/sync wiederholt mit kleinen Batches auf, bis remaining=0,
  // und summiert die Ergebnisse für die Anzeige.
  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    setSyncProgress(null);

    const acc = { employees: 0, manualContacts: 0, total: 0, added: 0, deleted: 0, unchanged: 0, errors: [] };
    let batch = 0;
    const MAX_BATCHES = 30; // Sicherheitsnetz gegen Endlosschleifen

    try {
      while (batch < MAX_BATCHES) {
        batch++;
        setSyncProgress({ batch, remaining: null, planTotal: null });
        const res = await syncDrucker(15);
        const d = res.data;
        acc.employees      = d.employees;
        acc.manualContacts = d.manualContacts;
        acc.total          = d.total;
        acc.added         += d.added;
        acc.deleted       += d.deleted;
        acc.unchanged      = d.unchanged;
        acc.errors.push(...(d.errors || []));
        setLastSync(d.syncedAt);
        setSyncProgress({ batch, remaining: d.remaining, planTotal: d.planTotal });

        if (!d.remaining || d.remaining <= 0) break;
      }
      setSyncResult(acc);
    } catch (err) {
      setSyncResult(acc.added || acc.deleted || acc.errors.length ? acc : null);
      setSyncError(err.response?.data?.error || err.message);
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  // ── CSV-Download (Fallback) ────────────────────────────────────────────────
  const handleDownloadCSV = () => {
    const base = process.env.REACT_APP_API_URL || "/api";
    window.open(`${base}/drucker/csv`, "_blank");
  };

  // ── Zeilen aufbauen ────────────────────────────────────────────────────────
  const hrRows = employees.map((e) => ({
    name:       `${e.first_name || ""} ${e.last_name || ""}`.trim(),
    email:      (e.email || "").toLowerCase(),
    department: e.department || "",
  }));

  const activeHR    = hrRows.filter((r) => !excluded.includes(r.email));
  const activeTotal = activeHR.length + manual.length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Kopf ── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>🖨️ Drucker-Adressbuch</h1>
        <p style={{ color: "#666", marginTop: 4 }}>
          Canon iR-ADV C3826i &nbsp;·&nbsp;
          <strong>{activeTotal}</strong> Kontakt{activeTotal !== 1 ? "e" : ""} im Adressbuch
          {lastSync && (
            <span style={{ color: "#999", marginLeft: 12 }}>
              · Letzter Sync: {formatDate(lastSync)}
            </span>
          )}
        </p>
      </div>

      {/* ── Sync-Panel ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 12px" }}>
          Adressbuch synchronisieren
        </h2>
        <p style={{ fontSize: 13, color: "#666", margin: "0 0 14px" }}>
          Überträgt alle aktiven Mitarbeiter (ohne ausgeblendete) und manuellen Einträge
          direkt in den Drucker. Drucker-IP und Passwort müssen unter{" "}
          <strong>Einstellungen → Drucker</strong> hinterlegt sein.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <button
            onClick={handleSync}
            disabled={syncing || loading}
            style={{
              padding: "8px 18px", fontSize: 14, fontWeight: 700, borderRadius: 7,
              background: syncing ? "#ccc" : "#0078d4", color: "#fff", border: "none",
              cursor: syncing ? "default" : "pointer",
            }}
          >
            {syncing ? "⏳ Wird synchronisiert…" : "🖨️ Jetzt in Drucker übertragen"}
          </button>
          <button
            onClick={handleTest}
            disabled={testing}
            style={{ ...S.btn("#f3f2f1", "#323130", "1px solid #d2d0ce"), padding: "8px 14px", fontSize: 13 }}
          >
            {testing ? "⏳ Prüfe…" : "🔌 Verbindung testen"}
          </button>
          <button
            onClick={handleDownloadCSV}
            style={{ ...S.btn("#f3f2f1", "#323130", "1px solid #d2d0ce"), padding: "8px 14px", fontSize: 13 }}
          >
            ⬇️ CSV herunterladen (manuell)
          </button>
          <button
            onClick={handleProbe}
            disabled={probing}
            title="Zeigt welche Drucker-Pfade antworten (Diagnose)"
            style={{ ...S.btn("#fff8e1", "#7d5700", "1px solid #f0d090"), padding: "8px 14px", fontSize: 13 }}
          >
            {probing ? "⏳ Scanne…" : "🔍 Pfade scannen"}
          </button>
        </div>

        {syncing && (
          <ProgressBar
            label={
              syncProgress?.planTotal != null
                ? `Adressbuch wird übertragen… Batch ${syncProgress.batch} (noch ${syncProgress.remaining} von ${syncProgress.planTotal} Änderungen)`
                : `Adressbuch wird übertragen… (Batch ${syncProgress?.batch || 1})`
            }
          />
        )}

        {syncResult && (
          <div style={{
            padding: "10px 14px", borderRadius: 8, fontSize: 14,
            background: syncResult.errors?.length ? "#fff4ce" : "#dff6dd",
            color: syncResult.errors?.length ? "#7d5700" : "#107c10",
          }}>
            {syncResult.errors?.length ? "⚠️" : "✅"} Sync-Ziel: {syncResult.employees} Mitarbeiter + {syncResult.manualContacts} manuelle Kontakte
            = <strong>{syncResult.total} Einträge</strong>
            <div style={{ marginTop: 4 }}>
              Tatsächlich am Drucker: <strong>+{syncResult.added}</strong> hinzugefügt, <strong>-{syncResult.deleted}</strong> gelöscht, {syncResult.unchanged} unverändert
              {syncResult.errors?.length > 0 && <> — <strong>{syncResult.errors.length} Fehler</strong></>}
            </div>
            {syncResult.errors?.length > 0 && (
              <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, fontSize: 12, color: "#7d5700" }}>
                {syncResult.errors.slice(0, 15).map((e, i) => <li key={i}>{e}</li>)}
                {syncResult.errors.length > 15 && <li>… und {syncResult.errors.length - 15} weitere</li>}
              </ul>
            )}
          </div>
        )}
        {syncError && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fde7e9", color: "#d13438", fontSize: 14 }}>
            ❌ {syncError}
            <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c" }}>
              Tipp: CSV herunterladen und manuell über die Remote UI des Druckers importieren.
            </div>
          </div>
        )}
        {testResult && (
          <div style={{
            marginTop: 8, padding: "8px 14px", borderRadius: 8, fontSize: 13,
            background: testResult.ok ? "#dff6dd" : "#fde7e9",
            color:      testResult.ok ? "#107c10" : "#d13438",
          }}>
            {testResult.msg}
          </div>
        )}

        {probeResult && (
          <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "#f8f8f8", border: "1px solid #e0e0e0", fontSize: 12 }}>
            <strong style={{ fontSize: 13 }}>🔍 Pfad-Scan Ergebnis</strong>
            <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["URL", "Status", "Redirect", "Inhalt"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "4px 8px", background: "#f0f0f0", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {probeResult.map((r, i) => (
                  <tr key={i} style={{ background: r.status === 200 ? "#dff6dd" : r.status >= 300 && r.status < 400 ? "#fff4e0" : "transparent" }}>
                    <td style={{ padding: "3px 8px", fontFamily: "monospace", fontSize: 11 }}>{r.url}</td>
                    <td style={{ padding: "3px 8px", fontWeight: r.status === 200 ? 700 : 400 }}>{r.error ? "ERR" : r.status}</td>
                    <td style={{ padding: "3px 8px", color: "#666" }}>{r.redirect || ""}</td>
                    <td style={{ padding: "3px 8px", color: "#888", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.error || r.snippet || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: "#fde7e9", color: "#d13438", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          ❌ {error}
        </div>
      )}
      {loading && <ProgressBar label="Mitarbeiter werden geladen…" />}

      {/* ── Tabelle: HRLab-Mitarbeiter ── */}
      {!loading && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
              Mitarbeiter (HRLab) &nbsp;
              <span style={{ fontWeight: 400, color: "#888", fontSize: 13 }}>
                {activeHR.length} aktiv · {excluded.length} ausgeblendet
              </span>
            </h2>
          </div>
          <div style={{ overflowX: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["#", "Name", "E-Mail", "Abteilung", "Im Drucker", ""].map((h) => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hrRows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...S.td, textAlign: "center", color: "#999", padding: 24 }}>
                      Keine aktiven Mitarbeiter gefunden.
                    </td>
                  </tr>
                )}
                {(() => {
                  let activeIdx = 0;
                  return hrRows.map((row) => {
                    const isExcluded = excluded.includes(row.email);
                    if (!isExcluded) activeIdx++;
                    const nr = isExcluded ? "–" : String(activeIdx).padStart(3, "0");
                    return (
                      <tr key={row.email} style={{ opacity: isExcluded ? 0.4 : 1 }}>
                        <td style={{ ...S.td, color: "#999", fontFamily: "monospace" }}>{nr}</td>
                        <td style={{ ...S.td, fontWeight: 500 }}>{row.name}</td>
                        <td style={{ ...S.td, color: "#888" }}>{row.email}</td>
                        <td style={S.td}>{row.department || "—"}</td>
                        <td style={S.td}>
                          {isExcluded
                            ? <span style={{ color: "#d13438", fontWeight: 600 }}>✗ Ausgeblendet</span>
                            : <span style={{ color: "#107c10", fontWeight: 600 }}>✓ Aktiv</span>}
                        </td>
                        <td style={S.td}>
                          <button
                            style={S.btn(
                              isExcluded ? "#dff6dd" : "#fde7e9",
                              isExcluded ? "#107c10" : "#d13438",
                              `1px solid ${isExcluded ? "#a8dba8" : "#f3c0c2"}`
                            )}
                            onClick={() => handleToggleExclude(row.email)}
                          >
                            {isExcluded ? "+ Einblenden" : "− Ausblenden"}
                          </button>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tabelle: Manuelle Einträge ── */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>
            Manuelle Einträge &nbsp;
            <span style={{ fontWeight: 400, color: "#888", fontSize: 13 }}>{manual.length} Einträge</span>
          </h2>
          <button
            style={{ ...S.btn("#f3f2f1", "#323130", "1px solid #d2d0ce"), padding: "6px 12px", fontSize: 13 }}
            onClick={() => setShowModal(true)}
          >
            ➕ Hinzufügen
          </button>
        </div>
        {manual.length === 0 ? (
          <p style={{ color: "#999", fontSize: 13 }}>
            Keine manuellen Einträge. Über "Hinzufügen" externe Kontakte ergänzen
            (z.B. externe Stellen ohne HRLab-Eintrag).
          </p>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid #e1e4e8", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["#", "Name", "E-Mail", ""].map((h) => <th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {manual.map((m, i) => (
                  <tr key={m.id}>
                    <td style={{ ...S.td, color: "#999", fontFamily: "monospace" }}>
                      {String(activeHR.length + i + 1).padStart(3, "0")}
                    </td>
                    <td style={{ ...S.td, fontWeight: 500 }}>{m.name}</td>
                    <td style={{ ...S.td, color: "#888" }}>{m.email}</td>
                    <td style={S.td}>
                      <button
                        style={S.btn("#fde7e9", "#d13438", "1px solid #f3c0c2")}
                        onClick={() => handleDeleteManual(m.id)}
                      >
                        🗑️ Entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal: Manuellen Kontakt hinzufügen ── */}
      {showModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200,
          }}
        >
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 400, maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 17 }}>🖨️ Kontakt hinzufügen</h3>
            <p style={{ fontSize: 13, color: "#666", margin: "0 0 16px" }}>
              Wird in der Datenbank gespeichert und beim nächsten Sync automatisch übertragen.
            </p>

            <label style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>Name</label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Max Mustermann"
              style={{ display: "block", width: "100%", padding: "7px 10px", fontSize: 13, borderRadius: 6, border: "1px solid #d2d0ce", marginTop: 4, marginBottom: 12, boxSizing: "border-box" }}
            />

            <label style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>E-Mail-Adresse</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddManual(); }}
              placeholder="max@example.de"
              style={{ display: "block", width: "100%", padding: "7px 10px", fontSize: 13, borderRadius: 6, border: "1px solid #d2d0ce", marginTop: 4, marginBottom: 20, boxSizing: "border-box" }}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={{ ...S.btn("#f3f2f1", "#323130", "1px solid #d2d0ce"), padding: "7px 14px" }} onClick={() => setShowModal(false)}>
                Abbrechen
              </button>
              <button
                style={{ ...S.btn("#0078d4", "#fff"), padding: "7px 14px", opacity: (!newName.trim() || !newEmail.trim() || saving) ? 0.5 : 1 }}
                onClick={handleAddManual}
                disabled={!newName.trim() || !newEmail.trim() || saving}
              >
                {saving ? "⏳ Speichern…" : "Hinzufügen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
